import React, { useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Users, Activity, Stethoscope, FlaskConical, Scan, Pill, Settings, LogOut, Menu, X, User as UserIcon, FileText, Clock, Baby } from 'lucide-react';
export const DashboardLayout: React.FC = () => {
  const {
    currentUser,
    logout
  } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-19" />;
  }
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const getNavItems = () => {
    const items = [];
    if (currentUser.role === 'Administrator') {
      items.push({
        name: 'System Overview',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
      items.push({
        name: 'Staff Management',
        path: '/dashboard/staff',
        icon: Settings
      });
      items.push({
        name: 'Activity Log',
        path: '/dashboard/activity-log',
        icon: Clock
      });
    } else if (currentUser.role === 'Clinician') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else if (currentUser.role === 'Nursing') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
    }
    return items;
  };
  const navItems = getNavItems();
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Administrator':
        return 'bg-red-100 text-red-800';
      case 'Clinician':
        return 'bg-blue-100 text-blue-800';
      case 'Nursing':
        return 'bg-green-100 text-green-800';
      case 'Laboratory':
        return 'bg-yellow-100 text-yellow-800';
      case 'Radiology':
        return 'bg-purple-100 text-purple-800';
      case 'Pharmacy':
        return 'bg-teal-100 text-teal-800';
      case 'Reception':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="min-h-screen bg-gray-100 flex" data-id="element-20">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 flex md:hidden" data-id="element-21">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-22"></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white" data-id="element-23">
            <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-24">
              <button className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-25">
                <X className="h-6 w-6 text-white" data-id="element-26" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto" data-id="element-27">
              <div className="flex-shrink-0 flex items-center px-4" data-id="element-28">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 object-contain" data-id="element-29" />
                <span className="ml-2 text-lg font-bold text-gray-900" data-id="element-30">
                  BMED
                </span>
              </div>
              <nav className="mt-5 px-2 space-y-1" data-id="element-31">
                {navItems.map(item => {
              const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
              return <a key={item.name} href={item.path} onClick={e => {
                e.preventDefault();
                navigate(item.path);
                setSidebarOpen(false);
              }} className={`${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'} group flex items-center px-2 py-2 text-base font-medium rounded-md`} data-id="element-32">
                      <item.icon className={`${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'} mr-4 flex-shrink-0 h-6 w-6`} data-id="element-33" />
                      {item.name}
                    </a>;
            })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4" data-id="element-34">
              <div className="flex items-center" data-id="element-35">
                <div data-id="element-36">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-37">
                    <UserIcon className="h-6 w-6 text-gray-500" data-id="element-38" />
                  </div>
                </div>
                <div className="ml-3" data-id="element-39">
                  <p className="text-base font-medium text-gray-700" data-id="element-40">
                    {currentUser.fullName}
                  </p>
                  <p className="text-sm font-medium text-gray-500" data-id="element-41">
                    {currentUser.role}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 print:hidden" data-id="element-42">
        <div className="flex flex-col w-64" data-id="element-43">
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white" data-id="element-44">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto" data-id="element-45">
              <div className="flex items-center flex-shrink-0 px-4" data-id="element-46">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 object-contain" data-id="element-47" />
                <div className="ml-2" data-id="element-48">
                  <span className="text-lg font-bold text-gray-900 block leading-tight" data-id="element-49">
                    BMED
                  </span>
                  <span className="text-xs text-pink-600 font-medium" data-id="element-50">
                    Medical Centre
                  </span>
                </div>
              </div>
              <nav className="mt-8 flex-1 px-2 bg-white space-y-1" data-id="element-51">
                {navItems.map(item => {
                const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
                return <a key={item.name} href={item.path} onClick={e => {
                  e.preventDefault();
                  navigate(item.path);
                }} className={`${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'} group flex items-center px-2 py-2 text-sm font-medium rounded-md`} data-id="element-52">
                      <item.icon className={`${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'} mr-3 flex-shrink-0 h-5 w-5`} data-id="element-53" />
                      {item.name}
                    </a>;
              })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4" data-id="element-54">
              <div className="flex-shrink-0 w-full group block" data-id="element-55">
                <div className="flex items-center" data-id="element-56">
                  <div data-id="element-57">
                    <div className="inline-block h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-58">
                      <UserIcon className="h-5 w-5 text-gray-500" data-id="element-59" />
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-60">
                    <p className="text-sm font-medium text-gray-700" data-id="element-61">
                      {currentUser.fullName}
                    </p>
                    <p className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block mt-1 ${getRoleColor(currentUser.role)}`} data-id="element-62">
                      {currentUser.role}
                    </p>
                  </div>
                </div>
                <button onClick={handleLogout} className="mt-4 w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-63">
                  <LogOut className="mr-2 h-4 w-4" data-id="element-64" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden" data-id="element-65">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 flex justify-between bg-white border-b border-gray-200 pb-2 print:hidden" data-id="element-66">
          <button className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500" onClick={() => setSidebarOpen(true)} data-id="element-67">
            <span className="sr-only" data-id="element-68">Open sidebar</span>
            <Menu className="h-6 w-6" data-id="element-69" />
          </button>
          <div className="flex items-center pr-4" data-id="element-70">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getRoleColor(currentUser.role)}`} data-id="element-71">
              {currentUser.role}
            </span>
          </div>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none" data-id="element-72">
          <div className="py-6" data-id="element-73">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-74">
              <Outlet data-id="element-75" />
            </div>
          </div>
        </main>
      </div>
    </div>;
};